import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';
import minMax from 'dayjs/esm/plugin/minMax';
import duration from 'dayjs/esm/plugin/duration';
import { TimeZoneSelect, timezonesWithGMT } from './timezones';
import { AddressTypeModel, BookingAddressInfoModel, BookingAddressModel } from '@railmybox/api-booking';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(duration);

export class DateRangeType {
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
}

export function calculateWarehouseDateRange(
  railTransportDate: string,
  waypoints: BookingAddressInfoModel[],
  arrivalDate: string,
  terminalCutOffTime: string,
  isExport: boolean,
  isImport: boolean,
  isCustoms: boolean,
  isDangerousGoods: boolean,
  currentSequence: number,
  bookingClosed: string,
  isDomestic?: boolean,
  isFirstStationInDomestic?: boolean
): DateRangeType {
  const shipmentDate = new Date(railTransportDate);
  const { warehouseAfterStationBreakpoint, warehouseBeforeStationBreakpoint } = checkWarehouseAndStationPosition(waypoints);

  if (!isExport && currentSequence <= warehouseBeforeStationBreakpoint && warehouseBeforeStationBreakpoint !== -1) {
    return {
      minDate: dayjs(shipmentDate).subtract(3, 'day').startOf('day'),
      maxDate: dayjs(bookingClosed),
    };
  } else if (!isImport && currentSequence >= warehouseAfterStationBreakpoint && warehouseAfterStationBreakpoint !== -1) {
    return {
      minDate: dayjs(arrivalDate).add(8, 'hour'),
      maxDate: undefined,
    };
  } else {
    return calculateDateRange(
      railTransportDate,
      isExport,
      isCustoms,
      isDangerousGoods,
      arrivalDate,
      terminalCutOffTime,
      currentSequence === waypoints.length && waypoints[currentSequence - 1]?.type == AddressTypeModel.Station,
      waypoints,
      isDomestic,
      isFirstStationInDomestic
    );
  }
}

export function calculateDateRange(
  railTransportDate: string,
  isExport: boolean,
  isCustoms: boolean,
  isDangerousGoods: boolean,
  arrivalDate: string,
  terminalCutOffTime?: string,
  railstationPickup = false,
  waypoints?: BookingAddressModel[],
  isDomestic?: boolean,
  isFirstStationInDomestic?: boolean
): DateRangeType {
  const shipmentDate = dayjs(railTransportDate);
  const trainArrival = dayjs(arrivalDate);

  const arrivalDay = trainArrival.day();
  const isSunday = arrivalDay === 0;
  const isSaturday = arrivalDay === 6;

  // For export everything is easy. You can deliver up to 3 days in advance until the terminal cutoff
  if (isExport || (isDomestic && isFirstStationInDomestic)) {
    const minDate = shipmentDate.startOf('day').add(isDangerousGoods ? -1 : -3, 'day');
    let maxDate = shipmentDate.endOf('day'); // TODO Cutoff
    if (terminalCutOffTime) {
      const dateStr = shipmentDate.format('YYYY-MM-DD');
      const fullCutoffLocal = `${dateStr}T${terminalCutOffTime}`;
      const cutOffLocal = dayjs(fullCutoffLocal);
      maxDate = cutOffLocal.subtract(6, 'hours');
    }
    return { minDate, maxDate };
  } else {
    // DEFAULT: minDate option is 8 hours after train arrival
    let minDate = trainArrival.add(8, 'hour');

    // if train arrival is the day after shipment, set to 7 AM next day
    if (trainArrival.date() - shipmentDate.date() == 1) {
      minDate = trainArrival.startOf('day').add(1, 'day').add(7, 'hour');
    }

    // if the train arrives on a weekend, move to Monday 1 PM
    if (isSaturday || isSunday) {
      const daysUntilNextMonday = (8 - arrivalDay) % 7 || 7;
      minDate = trainArrival.add(daysUntilNextMonday, 'day').startOf('day').add(13, 'hour');
    }

    if (railstationPickup) {
      minDate = trainArrival;
    }

    let maxDate;
    if (isDangerousGoods) {
      maxDate = trainArrival.add(4, 'day').endOf('day');
    } else if (isCustoms) {
      maxDate = shipmentDate.add(7, 'day').endOf('day');
    } else {
      maxDate = undefined;
    }

    // ensure maxDate is never before minDate
    if (maxDate && maxDate.isBefore(minDate)) {
      maxDate = minDate.endOf('day');
    }

    if (isLastWaypointStation(waypoints)) {
      minDate = trainArrival;
    }

    return { minDate, maxDate };
  }
}

function checkWarehouseAndStationPosition(waypoints: BookingAddressInfoModel[]): {
  warehouseAfterStationBreakpoint: number;
  warehouseBeforeStationBreakpoint: number;
} {
  const warehouses = waypoints.filter((wp) => wp.type === AddressTypeModel.Location);
  const stationIndex = waypoints.findIndex((wp) => wp.type === AddressTypeModel.Station);

  if (warehouses.length === 0 || stationIndex === -1) {
    return {
      warehouseAfterStationBreakpoint: -1,
      warehouseBeforeStationBreakpoint: -1,
    };
  }

  return {
    warehouseAfterStationBreakpoint: warehouses.some((wh) => waypoints.indexOf(wh) > stationIndex)
      ? warehouses.find((wh) => waypoints.indexOf(wh) > stationIndex)?.sequence
      : -1,
    warehouseBeforeStationBreakpoint: warehouses.some((wh) => waypoints.indexOf(wh) < stationIndex)
      ? warehouses.find((wh) => waypoints.indexOf(wh) < stationIndex)?.sequence
      : -1,
  };
}

function isLastWaypointStation(waypoints: BookingAddressModel[]) {
  return waypoints?.find((waypoint) => waypoint?.sequence === waypoints?.length)?.type === AddressTypeModel.Station;
}

export function vesselArrivalDateRange(
  isExport: boolean,
  plannedArrivalTime?: string,
  railTransportDate?: string,
  isRepositioning?: boolean
): DateRangeType {
  let minDate;
  let maxDate;

  if (isExport) {
    maxDate = undefined;

    if (isRepositioning) {
      minDate = dayjs(plannedArrivalTime).add(10, 'hour');

      const arrivalDay = minDate.day();
      const isSunday = arrivalDay === 0;
      const isSaturday = arrivalDay === 6;

      // if minDate is on a weekend, move to Monday 12 PM
      if (isSaturday || isSunday) {
        const daysUntilNextMonday = (8 - arrivalDay) % 7 || 7;
        minDate = minDate.add(daysUntilNextMonday, 'day').startOf('day').add(12, 'hour');
      }
    } else {
      minDate = dayjs(plannedArrivalTime).add(2, 'hour');
    }

    return { minDate, maxDate };
  } else {
    minDate = undefined;
    maxDate = dayjs(railTransportDate).add(-3, 'day').endOf('day');

    return { minDate, maxDate };
  }
}

export function guessTimezone(): string {
  return dayjs.tz.guess();
}

export function getTimeZoneList(): TimeZoneSelect[] {
  return timezonesWithGMT;
}
